import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useUserStore } from '../../lib/userStore';
import { placeTradeBid, supabase, getAuctionDetails, acceptTradeBid, rejectTradeBid } from '../../lib/supabase';
import toast from 'react-hot-toast';
// supabase client is not needed directly here; we use the shared HTTP endpoints configured in the app

interface QuickCounterOfferModalProps {
  auctionId: number;
  auctionInfo: {
    initiator_username: string;
    target_username: string;
    league_name: string;
    league_id?: number;
    initiator_user_id?: number;
    target_user_id?: number;
  };
  onClose: () => void;
  onSuccess: () => void;
}

export function QuickCounterOfferModal({ 
  auctionId, 
  auctionInfo, 
  onClose, 
  onSuccess 
}: QuickCounterOfferModalProps) {
  console.log('🎭 QuickCounterOfferModal render - auctionId:', auctionId, 'auctionInfo:', auctionInfo);
  
  const { user_id } = useUserStore();
  // Ensure auctionId is numeric where possible; some callers may pass string/undefined
  const numericAuctionId = Number(auctionId);
  const isAuctionIdValid = !(auctionId == null || auctionId === undefined || isNaN(numericAuctionId));
  const [localError, setLocalError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [targetSide, setTargetSide] = useState<'initiator' | 'target' | ''>('');
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [auctionDetails, setAuctionDetails] = useState<any>(null);
  const [actionLoadingBidId, setActionLoadingBidId] = useState<number | null>(null);
  // Normalized original asset ids (extracted from auctionDetails)
  const [originalSenderPlayerIds, setOriginalSenderPlayerIds] = useState<number[]>([]);
  const [originalReceiverPlayerIds, setOriginalReceiverPlayerIds] = useState<number[]>([]);
  // Map id -> display name (players & goalies)
  const [playerNameMap, setPlayerNameMap] = useState<Record<string, string>>({});

  // Assets dynamiques
  const [myPlayers, setMyPlayers] = useState<any[]>([]);
  const [myGoalies, setMyGoalies] = useState<any[]>([]);
  const [myPicks, setMyPicks] = useState<any[]>([]);
  const [targetPlayers, setTargetPlayers] = useState<any[]>([]);
  const [targetGoalies, setTargetGoalies] = useState<any[]>([]);
  const [targetPicks, setTargetPicks] = useState<any[]>([]);

  // Sélections
  const [selectedMyPlayerIds, setSelectedMyPlayerIds] = useState<number[]>([]);
  const [selectedMyGoalieIds, setSelectedMyGoalieIds] = useState<number[]>([]);
  const [selectedMyPickIds, setSelectedMyPickIds] = useState<number[]>([]);
  const [wantedPlayerIds, setWantedPlayerIds] = useState<number[]>([]);
  const [wantedGoalieIds, setWantedGoalieIds] = useState<number[]>([]);
  const [wantedPickIds, setWantedPickIds] = useState<number[]>([]);

  // Pour démo - IDs de joueurs de test
  // Deprecated demo states removed; we now use dynamic selections

  // Helpers pour ID et nom players
  const getPlayerId = (p: any) => (p.player_id ?? p.playerid ?? p.id);
  const getPlayerName = (p: any) => {
    if (p.firstname || p.lastname) return `${p.firstname ?? ''} ${p.lastname ?? ''}`.trim();
    return p.skaterfullname || p.goaliefullname || p.fullname || `Joueur #${getPlayerId(p)}`;
  };

  // Charger mes assets
  async function loadMyAssets() {
    if (!auctionInfo.league_id || !user_id) return;
    setLoadingAssets(true);
    try {
      // Joueurs et gardiens (vue getmyteamplayer)
      const { data: skaters, error: playersError } = await supabase
        .from('getmyteamplayer')
        .select('*')
        .eq('league_id', auctionInfo.league_id)
        .eq('user_id', Number(user_id));
      if (playersError && playersError.code !== 'PGRST116') throw playersError;

      const list = skaters || [];
      setMyPlayers(list.filter((p: any) => !p.is_goalie));
      setMyGoalies(list.filter((p: any) => p.is_goalie));

      // Choix de repêchage (deux étapes pour éviter reliance sur draftpicks!inner)
      const { data: picksLinkRows, error: picksLinkError } = await supabase
        .from('draftpicks_user_league')
        .select(`id, draftpicks_id`)
        .eq('league_id', auctionInfo.league_id)
        .eq('user_id', Number(user_id));
      if (picksLinkError && picksLinkError.code !== 'PGRST116') throw picksLinkError;

      const linkRows = picksLinkRows || [];
      if (linkRows.length === 0) {
        setMyPicks([]);
      } else {
        const pickIndexes = linkRows.map((r: any) => r.draftpicks_id).filter((v: any) => v != null);
        const { data: pickDetails, error: pickDetailsError } = await supabase
          .from('draft_picks')
          .select('pick_index, round_number, league_id, position_in_round, picked_at')
          .in('pick_index', pickIndexes)
          .eq('league_id', auctionInfo.league_id);
        if (pickDetailsError && pickDetailsError.code !== 'PGRST116') throw pickDetailsError;

        const details = pickDetails || [];
        const mapped = linkRows.map((r: any) => {
          const detail = details.find((d: any) => d.pick_index === r.draftpicks_id && d.league_id === auctionInfo.league_id);
          return {
            id: r.id,
            round: detail ? detail.round_number : null,
            year: detail && detail.picked_at ? new Date(detail.picked_at).getFullYear() : null,
            overall_pick: detail ? detail.position_in_round : null
          };
        });
        setMyPicks(mapped);
      }
    } catch (e) {
      console.error('Erreur chargement de mes assets', e);
      toast.error('Erreur lors du chargement de votre équipe');
    } finally {
      setLoadingAssets(false);
    }
  }

  // Charger assets du DG ciblé selon targetSide
  async function loadTargetAssets(side: 'initiator' | 'target') {
    if (!auctionInfo.league_id) return;
    const targetUserId = side === 'initiator' ? auctionInfo.initiator_user_id : auctionInfo.target_user_id;
    if (!targetUserId) return;
    setLoadingAssets(true);
    try {
      const { data: skaters, error: playersError } = await supabase
        .from('getmyteamplayer')
        .select('*')
        .eq('league_id', auctionInfo.league_id)
        .eq('user_id', targetUserId);
      if (playersError && playersError.code !== 'PGRST116') throw playersError;

      const list = skaters || [];
      setTargetPlayers(list.filter((p: any) => !p.is_goalie));
      setTargetGoalies(list.filter((p: any) => p.is_goalie));

      const { data: picksLinkRows, error: picksLinkError } = await supabase
        .from('draftpicks_user_league')
        .select(`id, draftpicks_id`)
        .eq('league_id', auctionInfo.league_id)
        .eq('user_id', targetUserId);
      if (picksLinkError && picksLinkError.code !== 'PGRST116') throw picksLinkError;

      const linkRows = picksLinkRows || [];
      if (linkRows.length === 0) {
        setTargetPicks([]);
      } else {
        const pickIndexes = linkRows.map((r: any) => r.draftpicks_id).filter((v: any) => v != null);
        const { data: pickDetails, error: pickDetailsError } = await supabase
          .from('draft_picks')
          .select('pick_index, round_number, league_id, position_in_round, picked_at')
          .in('pick_index', pickIndexes)
          .eq('league_id', auctionInfo.league_id);
        if (pickDetailsError && pickDetailsError.code !== 'PGRST116') throw pickDetailsError;

        const details = pickDetails || [];
        const mapped = linkRows.map((r: any) => {
          const detail = details.find((d: any) => d.pick_index === r.draftpicks_id && d.league_id === auctionInfo.league_id);
          return {
            id: r.id,
            round: detail ? detail.round_number : null,
            year: detail && detail.picked_at ? new Date(detail.picked_at).getFullYear() : null,
            overall_pick: detail ? detail.position_in_round : null
          };
        });
        setTargetPicks(mapped);
      }
    } catch (e) {
      console.error('Erreur chargement assets cible', e);
      toast.error('Erreur lors du chargement de l\'équipe ciblée');
    } finally {
      setLoadingAssets(false);
    }
  }

  // Charger les noms pour une liste d'IDs (recherche dans players puis goalies)
  async function loadNamesForIds(ids: Array<number | string>) {
    try {
      const uniqueIds = Array.from(new Set(ids.filter((i) => i != null).map(String)));
      const idsToFetch = uniqueIds.filter(id => !playerNameMap[id]);
      if (idsToFetch.length === 0) return;

      // Fetch skater names
      const { data: playersData, error: playersError } = await supabase
        .from('players')
        .select('playerid, skaterfullname')
        .in('playerid', idsToFetch as any[]);

      if (playersError && playersError.code !== 'PGRST116') console.warn('Erreur fetch players names', playersError);

      // Fetch goalies names
      const { data: goaliesData, error: goaliesError } = await supabase
        .from('goalies')
        .select('playerid, goaliefullname')
        .in('playerid', idsToFetch as any[]);

      if (goaliesError && goaliesError.code !== 'PGRST116') console.warn('Erreur fetch goalies names', goaliesError);

      const newMap: Record<string, string> = {};
      (playersData || []).forEach((p: any) => { if (p?.playerid) newMap[String(p.playerid)] = p.skaterfullname || String(p.playerid); });
      (goaliesData || []).forEach((g: any) => { if (g?.playerid) newMap[String(g.playerid)] = g.goaliefullname || String(g.playerid); });

      setPlayerNameMap((prev) => ({ ...prev, ...newMap }));
    } catch (e) {
      console.error('Erreur loadNamesForIds', e);
    }
  }

  // Charger mes assets au montage et recharger les assets cibles quand la cible change
  useEffect(() => {
    loadMyAssets();
    // charger details d'enchere pour récupérer original assets
    (async () => {
      try {
        if (!isAuctionIdValid) {
          console.warn('QuickCounterOfferModal: skip getAuctionDetails - invalid auctionId:', auctionId);
          setLocalError('ID d\'enchère manquant ou invalide');
          return;
        }

        const { data, error } = await getAuctionDetails(numericAuctionId);
        if (error) {
          console.error('Erreur chargement details enchere (RPC):', { auctionId, error });
          return;
        }
        if (data && data.success) {
          // Fusionner l'objet auction avec la liste bids renvoyée par la RPC
          setAuctionDetails({
            ...(data.auction || {}),
            bids: data.bids ?? []
          });
          // Collecter IDs depuis auction original et bids pour précharger les noms
          try {
            const bidIds: any[] = [];
            (data.bids || []).forEach((b: any) => {
              const s = b.bid_sender_assets || b.bid_sender_assets_json || {};
              const r = b.bid_receiver_assets || b.bid_receiver_assets_json || {};
              (s.players || []).forEach((x: any) => bidIds.push(x));
              (s.goalies || []).forEach((x: any) => bidIds.push(x));
              (s.picks || []).forEach((x: any) => bidIds.push(x));
              (r.players || []).forEach((x: any) => bidIds.push(x));
              (r.goalies || []).forEach((x: any) => bidIds.push(x));
              (r.picks || []).forEach((x: any) => bidIds.push(x));
            });
            const origSender = data.auction?.original_sender_assets?.players || [];
            const origReceiver = data.auction?.original_receiver_assets?.players || [];
            const allIds = [...bidIds, ...(origSender || []), ...(origReceiver || [])].filter((v: any) => v != null);
            if (allIds.length > 0) loadNamesForIds(allIds);
          } catch (e) {
            // ignore
          }
          try {
            console.debug('QuickCounterOfferModal - loaded auctionDetails (merged bids):', {
              auction: data.auction,
              bids: data.bids
            });
          } catch (e) {
            // ignore
          }

          // Extract numeric player IDs from original assets (if present)
          try {
            const sPlayersRaw = (data.auction?.original_sender_assets?.players) || [];
            const rPlayersRaw = (data.auction?.original_receiver_assets?.players) || [];
            const normalizeToNumber = (v: any) => {
              if (v == null) return null;
              if (typeof v === 'number') return v;
              if (typeof v === 'string' && v.trim() !== '' && !isNaN(Number(v))) return Number(v);
              if (typeof v === 'object') return (v.player_id ?? v.playerid ?? v.id ?? null);
              return null;
            };
            const sIds = Array.isArray(sPlayersRaw) ? sPlayersRaw.map(normalizeToNumber).filter((x: any) => x != null).map(Number) : [];
            const rIds = Array.isArray(rPlayersRaw) ? rPlayersRaw.map(normalizeToNumber).filter((x: any) => x != null).map(Number) : [];
            setOriginalSenderPlayerIds(sIds as number[]);
            setOriginalReceiverPlayerIds(rIds as number[]);
            console.debug('QuickCounterOfferModal - normalized original player ids', { sIds, rIds });
          } catch (e) {
            console.warn('QuickCounterOfferModal - failed to normalize original player ids', e);
          }
        }
      } catch (e) {
        console.error('Erreur chargement details enchere (catch):', e);
      }
    })();
  }, []);

  // Fonction pour accepter une contre-offre
  const handleAcceptBid = async (bidId: number) => {
    if (!user_id) {
      toast.error('Vous devez être connecté');
      return;
    }
    setActionLoadingBidId(bidId);
    try {
      const { error } = await acceptTradeBid(bidId, Number(user_id));
      if (error) {
        toast.error('Erreur acceptation: ' + (error.message || 'Erreur inconnue'));
      } else {
        toast.success('✅ Contre-offre acceptée');
        // reload details
  // reload details
        try {
          const { data: refreshed, error: refError } = await getAuctionDetails(auctionId);
          if (!refError && refreshed && refreshed.success) {
            setAuctionDetails({ ...(refreshed.auction || {}), bids: refreshed.bids ?? [] });
            try {
              const bidIds: any[] = [];
              (refreshed.bids || []).forEach((b: any) => {
                const s = b.bid_sender_assets || b.bid_sender_assets_json || {};
                const r = b.bid_receiver_assets || b.bid_receiver_assets_json || {};
                (s.players || []).forEach((x: any) => bidIds.push(x));
                (s.goalies || []).forEach((x: any) => bidIds.push(x));
                (r.players || []).forEach((x: any) => bidIds.push(x));
                (r.goalies || []).forEach((x: any) => bidIds.push(x));
              });
              if (bidIds.length > 0) loadNamesForIds(bidIds);
            } catch (e) {
              // ignore
            }
          }
        } catch (e) {
          console.error('Erreur reload after accept', e);
        } finally {
          // finished reload
        }
        onSuccess();
        onClose();
      }
    } catch (e) {
      console.error('Erreur acceptBid', e);
      toast.error('Erreur lors de l\'acceptation');
    } finally {
      setActionLoadingBidId(null);
    }
  };

  // Fonction pour rejeter une contre-offre
  const handleRejectBid = async (bidId: number) => {
    if (!user_id) {
      toast.error('Vous devez être connecté');
      return;
    }
    setActionLoadingBidId(bidId);
    try {
      const { error } = await rejectTradeBid(bidId, Number(user_id));
      if (error) {
        toast.error('Erreur rejet: ' + (error.message || 'Erreur inconnue'));
      } else {
        toast.success('✖️ Contre-offre rejetée');
        // reload details
        try {
          const { data: refreshed, error: refError } = await getAuctionDetails(auctionId);
          if (!refError && refreshed && refreshed.success) {
            setAuctionDetails({ ...(refreshed.auction || {}), bids: refreshed.bids ?? [] });
          }
        } catch (e) {
          console.error('Erreur reload after reject', e);
        } finally {
          // no-op
        }
      }
    } catch (e) {
      console.error('Erreur rejectBid', e);
      toast.error('Erreur lors du rejet');
    } finally {
      setActionLoadingBidId(null);
    }
  };

  useEffect(() => {
    if (targetSide === 'initiator' || targetSide === 'target') {
      loadTargetAssets(targetSide);
      // Reset wanted selections when switching target
      setWantedPlayerIds([]);
      setWantedGoalieIds([]);
      setWantedPickIds([]);
    }
  }, [targetSide]);

  // Live validation: at least one wanted element must belong to the original transaction
  const isWantedIntersectingOriginal = () => {
    if (!auctionDetails) return true; // if unknown, allow
    
    // Debug logs pour comprendre le problème
    console.log('🔍 DEBUG isWantedIntersectingOriginal:', {
      targetSide,
      wantedPlayerIds,
      wantedGoalieIds,
      wantedPickIds,
      auctionDetails: auctionDetails
    });

    // Determine which side of the original transaction corresponds to the "Vous voulez" section
    const originalAssets = targetSide === 'initiator'
      ? (auctionDetails.original_sender_assets || {})
      : (auctionDetails.original_receiver_assets || {});

    const origPlayersRaw = originalAssets.players || [];
    const origPicksRaw = originalAssets.picks || [];
    const origGoaliesRaw = originalAssets.goalies || [];

    console.log('🔍 DEBUG originalAssets:', {
      targetSide,
      originalAssets,
      origPlayersRaw,
      origPicksRaw,
      origGoaliesRaw
    });

    const normalizeId = (v: any) => {
      if (v == null) return null;
      if (typeof v === 'number') return v;
      if (typeof v === 'string' && v.trim() !== '' && !isNaN(Number(v))) return Number(v);
      // object with id fields
      if (typeof v === 'object') return (v.player_id ?? v.playerid ?? v.id ?? v.goalie_id ?? v.pick_id ?? null);
      return null;
    };

    // Prefer the precomputed normalized IDs when available, but check both sender and receiver
    let origPlayers = [];
    if (originalSenderPlayerIds.length > 0 || originalReceiverPlayerIds.length > 0) {
      // Use precomputed IDs - combine both sender and receiver for comprehensive check
      origPlayers = [...originalSenderPlayerIds, ...originalReceiverPlayerIds];
    } else {
      // Fallback to raw processing
      origPlayers = origPlayersRaw.map(normalizeId).filter((x: any) => x != null);
    }
    
    const origPicks = origPicksRaw.map(normalizeId).filter((x: any) => x != null);
    const origGoalies = origGoaliesRaw.map(normalizeId).filter((x: any) => x != null);

    console.log('🔍 DEBUG normalized IDs:', {
      origPlayers,
      origPicks,
      origGoalies,
      originalSenderPlayerIds,
      originalReceiverPlayerIds
    });

    const playersIntersect = wantedPlayerIds.some(id => origPlayers.includes(Number(id)));
    const picksIntersect = wantedPickIds.some(id => origPicks.includes(Number(id)));
    const goaliesIntersect = wantedGoalieIds.some(id => origGoalies.includes(Number(id)));

    console.log('🔍 DEBUG intersections:', {
      playersIntersect,
      picksIntersect,
      goaliesIntersect
    });

    // If ID-based matching failed for players, try a name-based fallback:
    let nameMatch = false;
    if (!playersIntersect && wantedPlayerIds.length > 0 && targetPlayers && targetPlayers.length > 0) {
      try {
        const wantedNames = targetPlayers
          .filter((p: any) => wantedPlayerIds.map(String).includes(String(getPlayerId(p))))
          .map((p: any) => getPlayerName(p).toLowerCase().trim());

        const extractNameFromAsset = (a: any) => {
          if (!a) return null;
          if (typeof a === 'string') return a.toLowerCase();
          if (typeof a === 'number') return null;
          // object: try several fields
          if (a.firstname || a.lastname) return `${a.firstname ?? ''} ${a.lastname ?? ''}`.toLowerCase().trim();
          if (a.skaterfullname) return String(a.skaterfullname).toLowerCase().trim();
          if (a.goaliefullname) return String(a.goaliefullname).toLowerCase().trim();
          if (a.fullname) return String(a.fullname).toLowerCase().trim();
          if (a.name) return String(a.name).toLowerCase().trim();
          return null;
        };

        const origNames = origPlayersRaw.map(extractNameFromAsset).filter((n: any) => n != null);
        // any overlap?
        nameMatch = wantedNames.some((wn: string) => origNames.some((on: string) => on && on.includes(wn) || wn.includes(on)));
        
        console.log('🔍 DEBUG name matching:', {
          wantedNames,
          origNames,
          nameMatch
        });
      } catch (e) {
        // ignore fallback errors
        console.warn('Name fallback error', e);
      }
    }

    const result = playersIntersect || picksIntersect || goaliesIntersect || nameMatch;
    console.log('🔍 DEBUG final result:', result);
    return result;
  };

  const handleSubmit = async () => {
    if (!user_id) {
      toast.error('Vous devez être connecté');
      return;
    }

    if (auctionId == null || auctionId === undefined || isNaN(Number(auctionId))) {
      toast.error('ID d\'enchère manquant ou invalide');
      return;
    }

    setIsSubmitting(true);

    try {
      const bidOffer = {
        players: Array.isArray(selectedMyPlayerIds) ? selectedMyPlayerIds : [],
        goalies: Array.isArray(selectedMyGoalieIds) ? selectedMyGoalieIds : [],
        picks: Array.isArray(selectedMyPickIds) ? selectedMyPickIds : []
      };

      const bidRequest = {
        players: Array.isArray(wantedPlayerIds) ? wantedPlayerIds : [],
        goalies: Array.isArray(wantedGoalieIds) ? wantedGoalieIds : [],
        picks: Array.isArray(wantedPickIds) ? wantedPickIds : []
      };

      const totalOffered = bidOffer.players.length + bidOffer.goalies.length + bidOffer.picks.length;
      const totalWanted = bidRequest.players.length + bidRequest.goalies.length + bidRequest.picks.length;
      if (!targetSide) {
        toast.error('Sélectionnez le DG ciblé');
        return;
      }
      if (totalOffered === 0) {
        toast.error('Vous devez proposer au moins un élément');
        return;
      }
      if (totalWanted === 0) {
        toast.error('Vous devez demander au moins un élément');
        return;
      }

      // Enforce original transaction inclusion using the same logic as the validation function
      if (auctionDetails && !isWantedIntersectingOriginal()) {
        toast.error('Vous devez demander au moins un élément provenant de l\'échange original');
        return;
      }

      const result = await placeTradeBid(
        auctionId,
        Number(user_id),
        bidOffer,
        bidRequest,
        targetSide || undefined
      );

      if (result.error) {
        toast.error('Erreur: ' + (result.error.message || 'Erreur inconnue'));
      } else {
        toast.success('🔥 Contre-offre soumise avec succès!');
        onSuccess();
        onClose();
      }
    } catch (error: any) {
      console.error('Erreur contre-offre:', error);
      toast.error('Erreur lors de la soumission');
    } finally {
      setIsSubmitting(false);
    }
  };

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="modal-backdrop fade show"
        onClick={onClose}
        style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1040 }}
      />

      {/* Modal */}
      <div className="modal fade show d-block" style={{ zIndex: 1050 }} tabIndex={-1}>
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content">
            {/* Header */}
            <div className="modal-header bg-primary text-white">
              <div>
                <h5 className="modal-title d-flex align-items-center gap-2 mb-1">🔥 Contre-offre rapide</h5>
                <div className="small opacity-75">
                  Enchère: {auctionInfo.initiator_username} → {auctionInfo.target_username}
                </div>
                <div className="small opacity-50">🏒 {auctionInfo.league_name}</div>
              </div>
              <button type="button" className="btn btn-close btn-close-white" onClick={onClose} aria-label="Close" />
            </div>

            {/* Body */}
            <div className="modal-body">
              {/* Explications */}
              <div className="alert alert-info mb-4 d-flex align-items-start gap-2">
                <span>💡</span>
                <div className="small">
                  <strong>Contre-offre rapide</strong>
                  <p className="mb-0">Choisissez le DG ciblé, puis sélectionnez dans votre équipe ce que vous offrez, et chez l'autre DG ce que vous voulez.</p>
                </div>
              </div>

              {/* Choix du DG ciblé */}
              <div className="p-3 border rounded mb-4" style={{ borderColor: 'rgba(59,130,246,.3)' }}>
                <h6 className="mb-2 text-primary">🎯 À qui envoyez-vous la contre-offre ?</h6>
                <div className="d-flex flex-column gap-2">
                  <label className="d-flex align-items-center gap-2">
                    <input
                      type="radio"
                      name="targetSide"
                      value="initiator"
                      className="form-check-input"
                      checked={targetSide === 'initiator'}
                      onChange={() => setTargetSide('initiator')}
                    />
                    <span className="small">Au DG initiateur: {auctionInfo.initiator_username}</span>
                  </label>
                  <label className="d-flex align-items-center gap-2">
                    <input
                      type="radio"
                      name="targetSide"
                      value="target"
                      className="form-check-input"
                      checked={targetSide === 'target'}
                      onChange={() => setTargetSide('target')}
                    />
                    <span className="small">Au DG cible: {auctionInfo.target_username}</span>
                  </label>
                </div>
              </div>

                <div className="row g-3 mb-4">
                  {/* Ce que vous offrez (mon équipe) */}
                  <div className="col-12 col-md-6">
                    <div className="p-3 border rounded" style={{ borderColor: 'rgba(34,197,94,.3)' }}>
                      <h6 className="mb-3 text-success">💰 Vous offrez (Votre équipe)</h6>
                      {loadingAssets && myPlayers.length === 0 && myGoalies.length === 0 && myPicks.length === 0 ? (
                        <div className="text-center py-2"><span className="spinner-border spinner-border-sm" /> Chargement...</div>
                      ) : (
                        <>
                          <div className="mb-2 small text-muted">Joueurs</div>
                          <div className="d-flex flex-column gap-1 mb-3" style={{ maxHeight: 140, overflowY: 'auto' }}>
                            {myPlayers.map((p) => (
                              <label key={getPlayerId(p)} className="d-flex align-items-center gap-2">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={selectedMyPlayerIds.includes(getPlayerId(p))}
                                  onChange={(e) => {
                                    const id = getPlayerId(p);
                                    setSelectedMyPlayerIds((prev) => e.target.checked ? [...prev, id] : prev.filter(x => x !== id));
                                  }}
                                />
                                <span className="small">{getPlayerName(p)}</span>
                              </label>
                            ))}
                            {myPlayers.length === 0 && (<div className="small text-muted">Aucun joueur</div>)}
                          </div>

                          <div className="mb-2 small text-muted">Gardiens</div>
                          <div className="d-flex flex-column gap-1 mb-3" style={{ maxHeight: 100, overflowY: 'auto' }}>
                            {myGoalies.map((g) => (
                              <label key={getPlayerId(g)} className="d-flex align-items-center gap-2">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={selectedMyGoalieIds.includes(getPlayerId(g))}
                                  onChange={(e) => {
                                    const id = getPlayerId(g);
                                    setSelectedMyGoalieIds((prev) => e.target.checked ? [...prev, id] : prev.filter(x => x !== id));
                                  }}
                                />
                                <span className="small">{getPlayerName(g)}</span>
                              </label>
                            ))}
                            {myGoalies.length === 0 && (<div className="small text-muted">Aucun gardien</div>)}
                          </div>

                          <div className="mb-2 small text-muted">Choix de repêchage</div>
                          <div className="d-flex flex-column gap-1" style={{ maxHeight: 120, overflowY: 'auto' }}>
                            {myPicks.map((pk: any) => (
                              <label key={pk.id} className="d-flex align-items-center gap-2">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={selectedMyPickIds.includes(pk.id)}
                                  onChange={(e) => {
                                    const id = pk.id;
                                    setSelectedMyPickIds((prev) => e.target.checked ? [...prev, id] : prev.filter(x => x !== id));
                                  }}
                                />
                                <span className="small">{pk.round}e ronde {pk.year}{pk.overall_pick ? ` (#${pk.overall_pick})` : ''}</span>
                              </label>
                            ))}
                            {myPicks.length === 0 && (<div className="small text-muted">Aucun choix</div>)}
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Ce que vous voulez (équipe du DG ciblé) */}
                  <div className="col-12 col-md-6">
                    <div className="p-3 border rounded" style={{ borderColor: 'rgba(234,179,8,.3)' }}>
                      <h6 className="mb-3 text-warning">🎯 Vous voulez (Équipe de {targetSide === 'initiator' ? auctionInfo.initiator_username : targetSide === 'target' ? auctionInfo.target_username : '...'})</h6>
                      {!targetSide ? (
                        <div className="small text-muted">Sélectionnez d'abord un DG ciblé</div>
                      ) : loadingAssets && targetPlayers.length === 0 && targetGoalies.length === 0 && targetPicks.length === 0 ? (
                        <div className="text-center py-2"><span className="spinner-border spinner-border-sm" /> Chargement...</div>
                      ) : (
                        <>
                          <div className="mb-2 small text-muted">Joueurs</div>
                          <div className="d-flex flex-column gap-1 mb-3" style={{ maxHeight: 140, overflowY: 'auto' }}>
                            {targetPlayers.map((p) => (
                              <label key={getPlayerId(p)} className="d-flex align-items-center gap-2">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={wantedPlayerIds.includes(getPlayerId(p))}
                                  onChange={(e) => {
                                    const id = getPlayerId(p);
                                    setWantedPlayerIds((prev) => e.target.checked ? [...prev, id] : prev.filter(x => x !== id));
                                  }}
                                />
                                <span className="small">{getPlayerName(p)}</span>
                              </label>
                            ))}
                            {targetPlayers.length === 0 && (<div className="small text-muted">Aucun joueur</div>)}
                          </div>

                          <div className="mb-2 small text-muted">Gardiens</div>
                          <div className="d-flex flex-column gap-1 mb-3" style={{ maxHeight: 100, overflowY: 'auto' }}>
                            {targetGoalies.map((g) => (
                              <label key={getPlayerId(g)} className="d-flex align-items-center gap-2">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={wantedGoalieIds.includes(getPlayerId(g))}
                                  onChange={(e) => {
                                    const id = getPlayerId(g);
                                    setWantedGoalieIds((prev) => e.target.checked ? [...prev, id] : prev.filter(x => x !== id));
                                  }}
                                />
                                <span className="small">{getPlayerName(g)}</span>
                              </label>
                            ))}
                            {targetGoalies.length === 0 && (<div className="small text-muted">Aucun gardien</div>)}
                          </div>

                          <div className="mb-2 small text-muted">Choix de repêchage</div>
                          <div className="d-flex flex-column gap-1" style={{ maxHeight: 120, overflowY: 'auto' }}>
                            {targetPicks.map((pk: any) => (
                              <label key={pk.id} className="d-flex align-items-center gap-2">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={wantedPickIds.includes(pk.id)}
                                  onChange={(e) => {
                                    const id = pk.id;
                                    setWantedPickIds((prev) => e.target.checked ? [...prev, id] : prev.filter(x => x !== id));
                                  }}
                                />
                                <span className="small">{pk.round}e ronde {pk.year}{pk.overall_pick ? ` (#${pk.overall_pick})` : ''}</span>
                              </label>
                            ))}
                            {targetPicks.length === 0 && (<div className="small text-muted">Aucun choix</div>)}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

              {/* Récapitulatif */}
              <div className="p-3 rounded mb-3" style={{ background: 'var(--bg-secondary)' }}>
                <h6 className="mb-2">📋 Récapitulatif de votre offre</h6>
                <div className="small">
                  <span className="text-success">Vous donnez: {selectedMyPlayerIds.length + selectedMyGoalieIds.length + selectedMyPickIds.length} éléments</span>
                  <br />
                  <span className="text-warning">Vous recevez: {wantedPlayerIds.length + wantedGoalieIds.length + wantedPickIds.length} éléments</span>
                </div>
              </div>
              {/* Contre-offres existantes */}
              <div className="p-3 rounded mb-3 border" style={{ borderColor: 'rgba(34,197,94,0.06)' }}>
                <h6 className="mb-2">🎯 Contre-offres existantes</h6>
                {(!auctionDetails || !(auctionDetails.bids || []).length) ? (
                  <>
                    {console.debug && console.debug('QuickCounterOfferModal - no bids to show, auctionDetails:', auctionDetails)}
                    <div className="small text-muted">Aucune contre-offre</div>
                  </>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {(auctionDetails.bids || []).map((bid: any) => {
                      const bidId = bid.bid_id ?? bid.id ?? null;
                      // Determine receiver: prefer explicit field, fallback to target_side logic
                      let receiverId = bid.receiver_user_id ?? bid.receiverid ?? null;
                      if (!receiverId) {
                        // If target_side is 'initiator', the receiver is the initiator_user_id of the auction
                        // else if 'target', receiver is target_user_id. This mirrors how bids.target_side is used.
                        try {
                          if (bid.target_side === 'initiator') receiverId = auctionDetails.initiator_user_id;
                          else if (bid.target_side === 'target') receiverId = auctionDetails.target_user_id;
                        } catch (e) {
                          // ignore
                        }
                      }

                      const formatList = (arr: any[]) => {
                        if (!Array.isArray(arr) || arr.length === 0) return null;
                        return arr.map((v: any, idx: number) => {
                          // try to extract id-like values
                          const rawId = (typeof v === 'number' || typeof v === 'string') ? v : (v.player_id ?? v.playerid ?? v.id ?? v.pick_id ?? v.goalie_id ?? JSON.stringify(v));
                          const key = String(rawId);
                          const name = playerNameMap[key];
                          return (
                            <span key={idx} className="badge bg-dark text-white me-1 mb-1">{name ? name : `#${rawId}`}</span>
                          );
                        });
                      };

                      const sender = bid.bid_sender_assets ?? bid.bid_sender_assets_json ?? {};
                      const receiver = bid.bid_receiver_assets ?? bid.bid_receiver_assets_json ?? {};

                      return (
                        <div key={bidId ?? JSON.stringify(bid)} className="p-3 border rounded" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div>
                              <div className="font-weight-bold">{bid.bidder_username || `DG #${bid.bidder_user_id}`}</div>
                              <div className="small text-muted">{new Date(bid.created_at).toLocaleString('fr-CA', { dateStyle: 'medium', timeStyle: 'short' })}</div>
                            </div>
                            <div className="text-end">
                              {bid.is_winning && <span className="badge bg-success">Gagnante</span>}
                            </div>
                          </div>

                          <div className="row mt-2">
                            <div className="col-12 col-md-6">
                              <div className="small mb-1 text-muted">Offre</div>
                              <div className="mb-2">
                                <div className="small text-muted">Joueurs:</div>
                                <div>{formatList(sender.players) ?? <div className="small text-muted">—</div>}</div>
                              </div>
                              <div className="mb-2">
                                <div className="small text-muted">Gardiens:</div>
                                <div>{formatList(sender.goalies) ?? <div className="small text-muted">—</div>}</div>
                              </div>
                              <div>
                                <div className="small text-muted">Choix:</div>
                                <div>{formatList(sender.picks) ?? <div className="small text-muted">—</div>}</div>
                              </div>
                            </div>

                            <div className="col-12 col-md-6">
                              <div className="small mb-1 text-muted">Demande</div>
                              <div className="mb-2">
                                <div className="small text-muted">Joueurs:</div>
                                <div>{formatList(receiver.players) ?? <div className="small text-muted">—</div>}</div>
                              </div>
                              <div className="mb-2">
                                <div className="small text-muted">Gardiens:</div>
                                <div>{formatList(receiver.goalies) ?? <div className="small text-muted">—</div>}</div>
                              </div>
                              <div>
                                <div className="small text-muted">Choix:</div>
                                <div>{formatList(receiver.picks) ?? <div className="small text-muted">—</div>}</div>
                              </div>
                            </div>
                          </div>

                          <div className="d-flex gap-2 justify-content-end mt-3">
                            {(Number(user_id) === Number(receiverId)) && (
                              <>
                                <button className="btn btn-sm btn-success d-flex align-items-center gap-2" onClick={() => handleAcceptBid(bidId)} disabled={actionLoadingBidId !== null}>{actionLoadingBidId === bidId ? '...' : '✅ Accepter'}</button>
                                <button className="btn btn-sm btn-outline-danger" onClick={() => handleRejectBid(bidId)} disabled={actionLoadingBidId !== null}>{actionLoadingBidId === bidId ? '...' : '✖️ Rejeter'}</button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            {/* Inline validation message */}
            {targetSide && !isWantedIntersectingOriginal() && (
              <div
                className="mt-3 mb-3 d-flex align-items-center"
                style={{
                  background: 'linear-gradient(90deg, rgba(220,38,38,0.12), rgba(185,28,28,0.06))',
                  border: '1px solid rgba(185,28,28,0.6)',
                  color: '#b91c1c',
                  padding: '12px',
                  borderRadius: '6px',
                  fontWeight: 700,
                  fontSize: '0.95rem'
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#b91c1c" viewBox="0 0 24 24" style={{ marginRight: 8 }}>
                  <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm1 17h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                </svg>
                Vous devez avoir au moins un élément de la transaction initiale dans la section "Vous voulez" pour soumettre une contre-offre.
              </div>
            )}
            {/* Footer */}
            <div className="modal-footer">
              {localError && (
                <div className="me-auto text-danger small" style={{ paddingTop: 6 }}>{localError}</div>
              )}
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Annuler
              </button>
              <button
                type="button"
                className="btn btn-primary d-flex align-items-center gap-2"
                onClick={handleSubmit}
                disabled={!isAuctionIdValid || isSubmitting || !targetSide || !isWantedIntersectingOriginal()}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                    Soumission...
                  </>
                ) : (
                  <>
                    🔥 Soumettre la contre-offre
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}